import _ from 'lodash';
import React from 'react';
import { decorate, action, runInAction, observable, computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Icon, Progress } from 'semantic-ui-react';

import { displayError } from '@amzn/base-ui/dist/helpers/notification';

import CopyToClipboard from '../../helpers/CopyToClipboard';

const openWindow = (url, windowFeatures) => {
  return window.open(url, '_blank', windowFeatures);
};

class ScEnvSshConnRowExpanded extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.countDown = undefined;
      this.intervalId = undefined;
      this.expired = false;
      this.extending = false;
      this.processingId = undefined;
      this.windowsExpanded = false;
      this.unixExpanded = false;
      this.scpExpanded = false;
      this.vscodeExpanded = false;
    });
  }

  get isAppStreamEnabled() {
    return process.env.REACT_APP_IS_APP_STREAM_ENABLED === 'true';
  }

  get networkInterfaces() {
    const entries = this.props.networkInterfaces;
    if (_.isEmpty(entries)) return [];
    const result = [];
    _.forEach(entries, item => {
      if (item.publicDnsName && !this.isAppStreamEnabled)
        result.push({ value: item.publicDnsName, type: 'dns', scope: 'public', info: 'Public' });
      if (item.privateIp) result.push({ value: item.privateIp, type: 'ip', scope: 'private', info: 'Private' });
    });
    return result;
  }

  get environment() { return this.props.scEnvironment; }
  get keyName() { return this.props.keyName; }
  get keyId() { return this.props.keyId; }
  get envsStore() { return this.props.scEnvironmentsStore; }
  get connectionId() { return this.props.connectionId; }

  getConnectionStore() {
    return this.envsStore.getScEnvConnectionStore(this.environment.id);
  }

  componentDidMount() { this.startCountDown(); }
  componentWillUnmount() { this.clearInterval(); }

  clearInterval() {
    if (!_.isUndefined(this.intervalId)) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
    this.countDown = undefined;
  }

  handleWindowsExpanded = () => { runInAction(() => { this.windowsExpanded = !this.windowsExpanded; }); };
  handleUnixExpanded = () => { runInAction(() => { this.unixExpanded = !this.unixExpanded; }); };
  handleScpExpanded = () => { runInAction(() => { this.scpExpanded = !this.scpExpanded; }); };
  handleVscodeExpanded = () => { runInAction(() => { this.vscodeExpanded = !this.vscodeExpanded; }); };

  handleConnect = id => action(async () => {
    try {
      runInAction(() => { this.processingId = id; });
      const store = this.getConnectionStore();
      const urlObj = await store.createConnectionUrl(id);
      if (urlObj.url) {
        const newTab = openWindow('about:blank');
        newTab.location = urlObj.url;
      }
    } catch (error) {
      displayError(error);
    } finally {
      runInAction(() => { this.processingId = undefined; });
    }
  });

  handleExtend = async () => {
    const keyId = this.keyId;
    const connectionId = this.connectionId;
    if (_.isEmpty(keyId)) return;

    runInAction(() => { this.extending = true; });
    try {
      const store = this.getConnectionStore();
      await store.sendSshKey(connectionId, keyId);
      // Success — reset the countdown
      runInAction(() => {
        this.expired = false;
        if (!_.isUndefined(this.intervalId)) {
          clearInterval(this.intervalId);
          this.intervalId = undefined;
        }
        this.countDown = 60;
        this.startCountDown();
      });
    } catch (error) {
      displayError(error);
    } finally {
      runInAction(() => { this.extending = false; });
    }
  };

  startCountDown = () => {
    if (!_.isUndefined(this.intervalId)) return;
    this.countDown = 60;
    this.intervalId = setInterval(() => {
      runInAction(() => {
        if (this.countDown <= 0) {
          clearInterval(this.intervalId);
          this.intervalId = undefined;
          this.expired = true;
          return;
        }
        this.countDown -= 1;
      });
    }, 1000);
  };

  getProgressColor() {
    const countDown = this.countDown;
    if (countDown > 30) return 'green';
    if (countDown > 10) return 'yellow';
    return 'red';
  }

  render() {
    const interfaces = this.networkInterfaces;
    const keyName = this.keyName;
    const publicNetwork = interfaces.find(ni => ni.info === 'Public');
    const example = `ssh -i '${keyName}.pem' ec2-user@${publicNetwork?.value || '<hostname>'}`;

    return (
      <div className="connection-details fadeIn animated">
        {this.isAppStreamEnabled ? this.renderAppStreamInfo() : (
          <>
            <div className="connection-details-title"><Icon name="terminal" /> Connection Details</div>

            <div className="ssh-connect-section">
              {this.renderProgressBar()}

              {!this.isAppStreamEnabled && publicNetwork && (
                <div className="ssh-command-box" style={{ marginTop: '12px' }}>
                  <span className="credential-label">Command</span>
                  <code>{example}</code>
                  <CopyToClipboard text={example} className="ml2" />
                </div>
              )}
            </div>

            {this.renderInfo()}
          </>
        )}
      </div>
    );
  }

  renderProgressBar() {
    const countDown = this.countDown;
    const expired = this.expired;
    const extending = this.extending;
    const percent = expired ? 0 : ((countDown / 60) * 100);

    return (
      <div className="ssh-progress-section">
        <div className="ssh-progress-status">
          {expired
            ? 'Your connection window has closed. Press Reset to open a new one.'
            : 'Your connection is ready — use the command below to connect.'}
        </div>
        <div className="ssh-progress-row">
          <div className="ssh-progress-bar-container">
            <Progress
              percent={percent}
              size="small"
              color={expired ? 'red' : this.getProgressColor()}
              className="ssh-progress-bar"
            />
            <span className="ssh-progress-label">
              {expired ? 'Session expired' : `${countDown}s remaining`}
            </span>
          </div>
          <button
            className="connection-btn connection-btn-secondary"
            onClick={this.handleExtend}
            disabled={extending}
            style={{ whiteSpace: 'nowrap' }}
          >
            {extending
              ? <Icon name="spinner" loading style={{ margin: 0, lineHeight: 1, height: '1em' }} />
              : <Icon name="redo" style={{ margin: 0, lineHeight: 1, height: '1em' }} />
            }
            {expired ? 'Reset' : 'Extend'}
          </button>
        </div>
      </div>
    );
  }

  renderAppStreamInfo() {
    const interfaces = this.networkInterfaces;
    const network = interfaces[0];
    const connectionId = this.connectionId;

    return (
      <div>
        <div className="connection-details-title"><Icon name="server" /> Connection Details</div>
        <div className="credential-item">
          <span className="credential-label">Private IP</span>
          <span className="credential-value">{network?.value || 'N/A'}</span>
          <div className="credential-actions"><CopyToClipboard text={network?.value} className="ml2" /></div>
        </div>
        <div style={{ marginTop: '16px' }}>
          <button className="connection-btn connection-btn-primary" onClick={this.handleConnect(connectionId)}
            disabled={this.processingId} data-testid="connect-to-workspace-button">
            {this.processingId ? <Icon name="spinner" loading style={{ margin: 0, lineHeight: 1, height: '1em' }} /> : <Icon name="external" style={{ margin: 0, lineHeight: 1, height: '1em' }} />}
            Connect via AppStream
          </button>
        </div>
      </div>
    );
  }

  renderInfo() {
    const interfaces = this.networkInterfaces;
    const publicNetwork = interfaces.find(ni => ni.info === 'Public');
    const winExpanded = this.windowsExpanded;
    const unixExpanded = this.unixExpanded;
    const host = publicNetwork?.value || '<hostname>';
    const keyName = this.keyName;

    const scpUpload = `scp -i '${keyName}.pem' myfile.txt ec2-user@${host}:~/`;
    const scpDownload = `scp -i '${keyName}.pem' ec2-user@${host}:~/results.txt ./`;
    const scpUploadDir = `scp -i '${keyName}.pem' -r myfolder ec2-user@${host}:~/`;

    return (
      <div style={{ marginTop: '20px' }}>
        <div className="connection-downloads">
          <div className="connection-downloads-title">Details</div>
        </div>
        
        {publicNetwork && (
          <div className="credential-item">
            <span className="credential-label">Hostname</span>
            <span className="credential-value">{publicNetwork.value}</span>
            <div className="credential-actions"><CopyToClipboard text={publicNetwork.value} className="ml2" /></div>
          </div>
        )}
        
        <div className="credential-item">
          <span className="credential-label">Username</span>
          <span className="credential-value">ec2-user</span>
          <div className="credential-actions"><CopyToClipboard text="ec2-user" className="ml2" /></div>
        </div>
        
        <div className="credential-item">
          <span className="credential-label">Key File</span>
          <span className="credential-value">{keyName}.pem</span>
          <div className="credential-actions"><CopyToClipboard text={`${keyName}.pem`} className="ml2" /></div>
        </div>

        <div className="connection-downloads">
          <div className="connection-downloads-title">Connection Instructions</div>
        </div>

        <div className="connection-instructions">
          <div className={`connection-instructions-header ${winExpanded ? 'active' : ''}`} onClick={this.handleWindowsExpanded}>
            <Icon name={winExpanded ? 'chevron down' : 'chevron right'} />
            <span>Connecting from Windows</span>
          </div>
          {winExpanded && (
            <div className="connection-instructions-content">
              <ol>
                <li>Open <b>PowerShell</b> (search for it in the Start menu)</li>
                <li>Navigate to the folder where your SSH key was downloaded: <code>cd .\Downloads</code></li>
                <li>Copy the SSH command shown above and paste it into PowerShell</li>
                <li>If prompted to trust the host, type <code>yes</code> and press Enter</li>
              </ol>
            </div>
          )}

          <div className={`connection-instructions-header ${unixExpanded ? 'active' : ''}`} onClick={this.handleUnixExpanded} style={{ marginTop: '8px' }}>
            <Icon name={unixExpanded ? 'chevron down' : 'chevron right'} />
            <span>Connecting from Mac/Linux</span>
          </div>
          {unixExpanded && (
            <div className="connection-instructions-content">
              <ol>
                <li>Open <b>Terminal</b> (on Mac: search for "Terminal" in Spotlight)</li>
                <li>Navigate to your SSH key folder: <code>cd ~/Downloads</code></li>
                <li>Set key permissions (first time only): <code>chmod 400 {keyName}.pem</code></li>
                <li>Copy the SSH command shown above and paste it into Terminal</li>
                <li>If prompted to trust the host, type <code>yes</code> and press Enter</li>
              </ol>
            </div>
          )}
        </div>

        {publicNetwork && (
          <div className="connection-instructions" style={{ marginTop: '8px' }}>
            <div className={`connection-instructions-header ${this.scpExpanded ? 'active' : ''}`} onClick={this.handleScpExpanded}>
              <Icon name={this.scpExpanded ? 'chevron down' : 'chevron right'} />
              <span>Transferring Files (SCP)</span>
            </div>
            {this.scpExpanded && (
              <div className="connection-instructions-content">
                <p style={{ fontSize: '0.9em', color: '#4b5563', marginBottom: '12px' }}>
                  Use <b>SCP</b> (Secure Copy) to transfer files between your computer and the workspace.
                  Run these commands from the folder containing your SSH key.
                </p>

                <div className="connection-example">
                  <div className="connection-example-desc">Upload a file to your workspace:</div>
                  <div className="ssh-command-box" style={{ marginTop: '4px' }}>
                    <code>{scpUpload}</code>
                    <CopyToClipboard text={scpUpload} className="ml2" />
                  </div>
                </div>

                <div className="connection-example">
                  <div className="connection-example-desc">Download a file from your workspace:</div>
                  <div className="ssh-command-box" style={{ marginTop: '4px' }}>
                    <code>{scpDownload}</code>
                    <CopyToClipboard text={scpDownload} className="ml2" />
                  </div>
                </div>

                <div className="connection-example">
                  <div className="connection-example-desc">Upload an entire folder:</div>
                  <div className="ssh-command-box" style={{ marginTop: '4px' }}>
                    <code>{scpUploadDir}</code>
                    <CopyToClipboard text={scpUploadDir} className="ml2" />
                  </div>
                </div>

                <p style={{ fontSize: '0.82em', color: '#6b7280', marginTop: '12px', marginBottom: 0 }}>
                  Replace <code>myfile.txt</code>, <code>results.txt</code>, or <code>myfolder</code> with your actual file or folder names.
                  The <code>~/</code> refers to the home directory on your workspace.
                </p>
              </div>
            )}
          </div>
        )}

        {publicNetwork && (
          <div className="connection-instructions" style={{ marginTop: '8px' }}>
            <div className={`connection-instructions-header ${this.vscodeExpanded ? 'active' : ''}`} onClick={this.handleVscodeExpanded}>
              <Icon name={this.vscodeExpanded ? 'chevron down' : 'chevron right'} />
              <span>Connecting with VS Code (Remote SSH)</span>
            </div>
            {this.vscodeExpanded && (
              <div className="connection-instructions-content">
                <p style={{ fontSize: '0.9em', color: '#4b5563', marginBottom: '12px' }}>
                  You can use <b>Visual Studio Code</b> to edit files and run code directly on your workspace.
                  This gives you a full IDE experience with your remote machine.
                </p>
                <ol>
                  <li>Install <a href="https://code.visualstudio.com/" target="_blank" rel="noopener noreferrer">VS Code</a> if you haven&#39;t already</li>
                  <li>Install the <b>Remote - SSH</b> extension (search for <code>ms-vscode-remote.remote-ssh</code> in the Extensions panel)</li>
                  <li>Open the VS Code Command Palette (<code>Ctrl+Shift+P</code> on Windows, <code>Cmd+Shift+P</code> on Mac)</li>
                  <li>Type <b>Remote-SSH: Open SSH Configuration File</b> and select your config file (usually <code>~/.ssh/config</code>)</li>
                  <li>Add the following entry and save:</li>
                </ol>
                <div className="ssh-command-box" style={{ marginTop: '4px', whiteSpace: 'pre', lineHeight: 1.6 }}>
                  <code>{`Host ${host}\n  HostName ${host}\n  User ec2-user\n  IdentityFile ~/Downloads/${keyName}.pem`}</code>
                  <CopyToClipboard text={`Host ${host}\n  HostName ${host}\n  User ec2-user\n  IdentityFile ~/Downloads/${keyName}.pem`} className="ml2" />
                </div>
                <ol start="6" style={{ marginTop: '12px' }}>
                  <li>Open the Command Palette again and type <b>Remote-SSH: Connect to Host</b></li>
                  <li>Select your workspace hostname from the list</li>
                  <li>VS Code will open a new window connected to your workspace — you can browse files, edit code, and use the integrated terminal</li>
                </ol>
                <p style={{ fontSize: '0.82em', color: '#6b7280', marginTop: '12px', marginBottom: 0 }}>
                  Adjust the <code>IdentityFile</code> path if your SSH key is saved in a different location.
                  On Windows, use a path like <code>C:\Users\YourName\Downloads\{keyName}.pem</code>.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}

decorate(ScEnvSshConnRowExpanded, {
  networkInterfaces: computed, keyName: computed, keyId: computed, envsStore: computed, connectionId: computed,
  intervalId: observable, countDown: observable, unixExpanded: observable,
  windowsExpanded: observable, expired: observable, extending: observable, processingId: observable,
  scpExpanded: observable, vscodeExpanded: observable,
  startCountDown: action, clearInterval: action, handleExtend: action,
});

export default inject()(withRouter(observer(ScEnvSshConnRowExpanded)));
